import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  approvePage,
  bootstrapSeo,
  bulkApprovePages,
  bulkUpdatePages,
  fetchSeoHealth,
  generateAllPages,
  generatePage,
  listSeoPages,
  pagesHealth,
  patchPage,
  previewPageUrl,
  recomputeLinks,
  refreshTierFares,
  regeneratePageContent,
  syncLogostream,
  syncRouteAirlines,
  syncVisaAll,
} from '../../utils/adminSeo';

const PAGE_TYPES = ['', 'route', 'airport', 'airline', 'visa', 'country', 'destination'];

export default function SeoManagement({ embedded = false }) {
  const [health, setHealth] = useState(null);
  const [pages, setPages] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Group page-count by type so the user can see at-a-glance whether routes
  // / airports / airlines are actually populated. Computed from the current
  // (possibly filtered) result set.
  const countsByType = pages.reduce((acc, p) => {
    acc[p.page_type] = (acc[p.page_type] || 0) + 1;
    return acc;
  }, {});

  const load = async () => {
    setLoading(true);
    try {
      // limit=200 is the API max; 211 total pages fit in 2 round-trips when
      // a future bootstrap grows the catalog further.
      const params = { limit: 200 };
      if (filterType) params.page_type = filterType;
      if (filterStatus) params.publish_status = filterStatus;
      const [h, p, ih] = await Promise.all([
        fetchSeoHealth(),
        listSeoPages(params),
        pagesHealth(),
      ]);
      setHealth(h);
      setPages(p.pages || []);
      setTotalPages(p.total || 0);
      setIssues(ih.issues || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load SEO data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filterType, filterStatus]);

  const run = async (label, fn) => {
    try {
      const res = await fn();
      if (res?.success === false) {
        const parts = [];
        if (Array.isArray(res.errors) && res.errors.length) {
          parts.push(`Errors: ${res.errors.join('; ')}`);
        }
        if (Array.isArray(res.warnings) && res.warnings.length) {
          parts.push(`Warnings: ${res.warnings.join('; ')}`);
        }
        toast.error(`${label} failed — ${parts.join(' | ') || 'Quality gate failed'}`, { duration: 8000 });
        return;
      }
      const warnings = res?.warnings || [];
      if (warnings.length) {
        toast.success(`${label} OK · ${warnings.length} warning(s): ${warnings.join(', ')}`);
      } else {
        toast.success(`${label} OK`);
      }
      console.log(res);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg;
      if (detail && typeof detail === 'object') {
        const parts = [];
        if (Array.isArray(detail.errors) && detail.errors.length) {
          parts.push(`Errors: ${detail.errors.join('; ')}`);
        }
        if (Array.isArray(detail.warnings) && detail.warnings.length) {
          parts.push(`Warnings: ${detail.warnings.join('; ')}`);
        }
        msg = parts.length ? parts.join(' | ') : JSON.stringify(detail);
      } else {
        msg = detail || e.message || label;
      }
      toast.error(`${label} failed — ${msg}`, { duration: 8000 });
    }
  };

  const runBulkApprove = async (params = {}) => {
    const scope = params.page_type ? `${params.page_type} drafts` : 'all drafts';
    if (!window.confirm(`Run the quality gate on every ${scope}? Pages that pass will be auto-published.`)) {
      return;
    }
    try {
      toast.info(`Bulk approving ${scope}…`);
      const res = await bulkApprovePages({ ...params, limit: 500 });
      const { approved_count, failed_count, candidates, by_type } = res;
      const breakdown = Object.entries(by_type || {})
        .map(([t, c]) => `${t}: +${c.approved}/-${c.failed}`)
        .join(' · ');
      const msg = `Bulk approve done — ${approved_count}/${candidates} approved, ${failed_count} skipped (legit gate failures). ${breakdown}`;
      toast.success(msg, { duration: 10000 });
      console.log('Bulk approve:', res);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Bulk approve failed', { duration: 8000 });
    }
  };

  // ── Bulk selection helpers ──────────────────────────────────────────────
  const toggleRow = (id) => {
    setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };
  const toggleAllVisible = () => {
    const visibleIds = pages.map((p) => p.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : visibleIds);
  };
  const clearSelection = () => setSelectedIds([]);

  const runBulk = async (label, body, confirmMsg) => {
    if (!selectedIds.length) {
      toast.error('Select at least one page');
      return;
    }
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const res = await bulkUpdatePages({ ids: selectedIds, ...body });
      const breakdown = Object.entries(res.by_type || {})
        .map(([t, n]) => `${t}: ${n}`)
        .join(' · ');
      toast.success(
        `${label} — ${res.modified_count}/${res.matched_count} updated${breakdown ? ` (${breakdown})` : ''}`,
        { duration: 8000 },
      );
      clearSelection();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || `${label} failed`, { duration: 8000 });
    } finally {
      setBulkBusy(false);
    }
  };

  const visibleIds = pages.map((p) => p.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));

  if (loading && !health) return <div className="p-6">Loading SEO dashboard…</div>;

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 max-w-6xl mx-auto space-y-6'}>
      {!embedded && (
        <>
          <h1 className="text-2xl font-bold text-[#1B375B]">SEO / GEO Pages</h1>
          <p className="text-gray-600 text-sm">
            Travel entity pages. Cached in Mongo — no supplier API calls on public page load.
          </p>
        </>
      )}
      {embedded && <h2 className="text-xl font-semibold text-[#1B375B]">Pages</h2>}

      {health && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            ['Routes', health.routes],
            ['Airports', health.airports],
            ['Airlines', health.airlines],
            ['Visa', health.visa],
            ['Indexable', health.indexable_pages],
          ].map(([label, val]) => (
            <div key={label} className="bg-white rounded-lg border p-4 shadow-sm">
              <div className="text-xs text-gray-500">{label}</div>
              <div className="text-xl font-semibold">{val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" className="px-4 py-2 bg-[#1B375B] text-white rounded-lg text-sm" onClick={() => run('Bootstrap', bootstrapSeo)}>
          Bootstrap
        </button>
        <button type="button" className="px-4 py-2 border rounded-lg text-sm" onClick={() => run('Generate all', () => generateAllPages(2))}>
          Generate all (tier ≤2)
        </button>
        <button type="button" className="px-4 py-2 border rounded-lg text-sm" onClick={() => run('Logostream sync', () => syncLogostream('all'))}>
          Sync Logostream
        </button>
        <button type="button" className="px-4 py-2 border rounded-lg text-sm" onClick={() => run('Visa sync', syncVisaAll)}>
          Sync visa
        </button>
        <button type="button" className="px-4 py-2 border rounded-lg text-sm" onClick={() => run('Tier-1 fares', () => refreshTierFares(1))}>
          Fares tier 1
        </button>
        <button type="button" className="px-4 py-2 border rounded-lg text-sm" onClick={() => run('Tier-2 fares', () => refreshTierFares(2))}>
          Fares tier 2
        </button>
        <button type="button" className="px-4 py-2 border rounded-lg text-sm" onClick={() => run('Recompute links', () => recomputeLinks(false))}>
          Recompute links
        </button>
        <button
          type="button"
          data-testid="seo-sync-airlines-btn"
          className="px-4 py-2 border border-[#659A9B] text-[#1B375B] rounded-lg text-sm hover:bg-[#659A9B]/10"
          onClick={() => run('Route-airlines sync', () => syncRouteAirlines({ limit: 200, use_tripjack: true }))}
          title="Pull operating airlines per route from Tripjack — unlocks airline-page interlinking"
        >
          Sync route → airlines
        </button>
        <button
          type="button"
          data-testid="seo-bulk-approve-btn"
          className="px-4 py-2 bg-[#659A9B] text-white rounded-lg text-sm hover:opacity-90"
          onClick={() => runBulkApprove({})}
          title="Run the quality gate on every draft page; auto-publish those that pass"
        >
          Bulk approve drafts
        </button>
        {filterType && (
          <button
            type="button"
            data-testid="seo-bulk-approve-scoped-btn"
            className="px-4 py-2 bg-[#659A9B]/80 text-white rounded-lg text-sm hover:opacity-90"
            onClick={() => runBulkApprove({ page_type: filterType })}
          >
            Bulk approve {filterType} drafts
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center text-xs">
        <span className="text-gray-500 mr-1">
          Showing {pages.length}{totalPages ? ` of ${totalPages}` : ''} pages
        </span>
        {PAGE_TYPES.filter((t) => t).map((t) => {
          const count = countsByType[t] || 0;
          const isActive = filterType === t;
          return (
            <button
              type="button"
              key={t}
              data-testid={`seo-type-chip-${t}`}
              onClick={() => setFilterType(isActive ? '' : t)}
              className={`px-2.5 py-1 rounded-full border text-xs transition ${
                isActive
                  ? 'bg-[#1B375B] text-white border-[#1B375B]'
                  : count
                    ? 'bg-white text-gray-700 border-gray-300 hover:border-[#1B375B]'
                    : 'bg-gray-50 text-gray-400 border-gray-200'
              }`}
            >
              {t} <span className="ml-1 opacity-80">{count}</span>
            </button>
          );
        })}
        {filterType && (
          <button
            type="button"
            className="text-xs text-[#659A9B] underline ml-1"
            onClick={() => setFilterType('')}
            data-testid="seo-filter-clear"
          >
            clear
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center text-sm">
        <label>
          Type{' '}
          <select className="border rounded px-2 py-1" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            {PAGE_TYPES.map((t) => (
              <option key={t || 'all'} value={t}>{t || 'all'}</option>
            ))}
          </select>
        </label>
        <label>
          Status{' '}
          <select className="border rounded px-2 py-1" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">all</option>
            <option value="draft">draft</option>
            <option value="published">published</option>
          </select>
        </label>
        <button type="button" className="text-[#659A9B] underline" onClick={load}>Refresh</button>
      </div>

      {issues.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
          <strong>{issues.length} published pages with quality issues</strong>
          <ul className="mt-2 list-disc pl-5">
            {issues.slice(0, 8).map((i) => (
              <li key={`${i.type}-${i.slug}`}>{i.type}/{i.slug}: {(i.errors || []).join(', ')}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Bulk action bar — appears when any rows are selected */}
      {selectedIds.length > 0 && (
        <div
          data-testid="seo-bulk-action-bar"
          className="sticky top-0 z-10 bg-[#1B375B] text-white rounded-lg shadow-lg px-4 py-3 flex flex-wrap items-center gap-2"
        >
          <span className="text-sm font-semibold mr-2" data-testid="seo-bulk-selection-count">
            {selectedIds.length} selected
          </span>
          <button
            type="button"
            data-testid="seo-bulk-publish-btn"
            disabled={bulkBusy}
            onClick={() => runBulk('Bulk publish', { publish_status: 'published' }, `Publish ${selectedIds.length} page(s)?`)}
            className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 rounded text-xs font-bold disabled:opacity-50"
          >
            Bulk publish
          </button>
          <button
            type="button"
            data-testid="seo-bulk-unpublish-btn"
            disabled={bulkBusy}
            onClick={() => runBulk('Bulk unpublish', { publish_status: 'draft' }, `Unpublish ${selectedIds.length} page(s)? They will be removed from sitemap and SSR.`)}
            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 rounded text-xs font-bold disabled:opacity-50"
          >
            Bulk unpublish
          </button>
          <span className="mx-1 opacity-40">|</span>
          <button
            type="button"
            data-testid="seo-bulk-indexable-btn"
            disabled={bulkBusy}
            onClick={() => runBulk('Mark indexable', { indexable: true }, `Allow search engines to index ${selectedIds.length} page(s)?`)}
            className="px-3 py-1.5 bg-sky-500 hover:bg-sky-600 rounded text-xs font-bold disabled:opacity-50"
          >
            Mark indexable
          </button>
          <button
            type="button"
            data-testid="seo-bulk-noindex-btn"
            disabled={bulkBusy}
            onClick={() => runBulk('Mark noindex', { indexable: false }, `Set noindex on ${selectedIds.length} page(s)? They will be hidden from Google/Bing.`)}
            className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 rounded text-xs font-bold disabled:opacity-50"
          >
            Mark noindex
          </button>
          <button
            type="button"
            data-testid="seo-bulk-clear-btn"
            onClick={clearSelection}
            className="ml-auto px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded text-xs"
          >
            Clear
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-2 w-8">
                <input
                  type="checkbox"
                  data-testid="seo-select-all"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected; }}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible"
                />
              </th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-left">Slug</th>
              <th className="p-2">Status</th>
              <th className="p-2">Index</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((p) => (
              <tr key={p.id} className={`border-t ${selectedIds.includes(p.id) ? 'bg-[#659A9B]/5' : ''}`}>
                <td className="p-2 text-center">
                  <input
                    type="checkbox"
                    data-testid={`seo-row-checkbox-${p.id}`}
                    checked={selectedIds.includes(p.id)}
                    onChange={() => toggleRow(p.id)}
                    aria-label={`Select ${p.slug}`}
                  />
                </td>
                <td className="p-2">{p.page_type}</td>
                <td className="p-2">
                  <a href={p.canonical_path} target="_blank" rel="noreferrer" className="text-[#659A9B]">
                    {p.slug}
                  </a>
                </td>
                <td className="p-2 text-center">{p.publish_status}</td>
                <td className="p-2 text-center">{p.indexable ? 'yes' : 'no'}</td>
                <td className="p-2 space-x-2 whitespace-nowrap">
                  <a
                    href={previewPageUrl(p)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#659A9B] underline"
                  >
                    Preview
                  </a>
                  <button
                    type="button"
                    className="text-xs text-[#1B375B] underline"
                    onClick={() => run('Regenerate', () => regeneratePageContent(p.id))}
                  >
                    Regen AI
                  </button>
                  {p.publish_status !== 'published' && (
                    <button
                      type="button"
                      className="text-xs text-[#1B375B] underline"
                      onClick={() => run('Approve', () => approvePage(p.id))}
                    >
                      Approve
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-xs text-gray-600 underline"
                    onClick={() => run('Toggle index', () => patchPage(p.id, { indexable: !p.indexable }))}
                  >
                    {p.indexable ? 'Noindex' : 'Index'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages.length === 0 && <p className="p-4 text-gray-500 text-sm">No pages — run Bootstrap first.</p>}
      </div>
    </div>
  );
}
